import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Archive, ArchiveRestore, Plus, Save, Send, Trash2 } from 'lucide-react'
import { getAccounts } from '../../api/master'
import { deleteTransactionType, getTransactionType, saveTransactionType, setTransactionTypeActive } from '../../api/accounting'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { money } from '../sales/salesShared'
import { isHeading } from '../master/masterShared'
import { TransactionTypesGuide } from '../master/MasterGuides'
import { ACCOUNT_TYPES } from './accountingShared'
import { SHARES, split } from './transactionTypes'

const back = <BackLink to="/accounting/transaction-types" label="Transaction types" />
const EXAMPLE = 100

let lineKey = 0
const blankLine = (side, l = {}) => ({ key: ++lineKey, side, account_code: '', share: 'rest', share_value: '', label: '', can_change: false, ...l })

const SIDE_TEXT = {
  debit: { title: 'Debit', help: 'The accounts that go up for an asset or expense (or down for a liability, equity or revenue). For gift cards sold, that’s Cash.' },
  credit: { title: 'Credit', help: 'The accounts that go up for a liability, equity or revenue (or down for an asset or expense). For gift cards sold, that’s a Gift cards owed liability.' },
}

// One transaction type: its name and its debit and credit lines, each with
// how it shares the amount. A live example shows what $100 would post.
export default function TransactionTypeForm() {
  const { typeId } = useParams()
  const isNew = !typeId
  const nav = useNavigate()
  const { notify } = useToast()
  const [accounts, setAccounts] = useState(null)
  const [type, setType] = useState(isNew ? {} : null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [lines, setLines] = useState(() => (isNew ? [blankLine('debit'), blankLine('credit')] : []))
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  useEffect(() => { getAccounts().then((a) => setAccounts(Array.isArray(a) ? a : [])).catch(() => setAccounts([])) }, [])
  useEffect(() => {
    if (isNew) return
    getTransactionType(typeId).then((t) => {
      setType(t)
      setName(t.name)
      setDescription(t.description || '')
      setLines(t.lines.map((l) => blankLine(l.side, { account_code: l.account_code, share: l.share, share_value: l.share === 'rest' ? '' : String(l.share_value), label: l.label, can_change: l.can_change })))
    }).catch((e) => setError(e.message || 'Couldn’t load the transaction type.'))
  }, [typeId, isNew])

  // Entries post to accounts under a heading, never the heading itself.
  const grouped = useMemo(() => {
    const list = (accounts || []).filter((a) => !isHeading(a, accounts) && !/^\d{2}00$/.test(String(a.account_code)))
    return ACCOUNT_TYPES.map(([t, label]) => [label, list.filter((a) => a.type === t)]).filter(([, l]) => l.length)
  }, [accounts])
  const byCode = useMemo(() => Object.fromEntries((accounts || []).map((a) => [a.account_code, a])), [accounts])

  const filled = useMemo(() => lines.filter((l) => l.account_code), [lines])
  const example = useMemo(() => {
    for (const side of ['debit', 'credit']) {
      const s = filled.filter((l) => l.side === side)
      if (!s.length) return { note: `Add a ${side} account to see an example.` }
      const rests = s.filter((l) => l.share === 'rest').length
      if (rests !== 1) return { note: `Mark exactly one ${side} line “the rest”: it takes what’s left of the amount.` }
    }
    try { return { lines: split(filled, EXAMPLE) } } catch (e) { return { note: e.message } }
  }, [filled])

  const update = (key, patch) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const addLine = (side) => setLines((ls) => [...ls, blankLine(side, { share: ls.some((l) => l.side === side && l.share === 'rest') ? 'percent' : 'rest' })])
  const removeLine = (key) => setLines((ls) => ls.filter((l) => l.key !== key))

  const save = async () => {
    if (!name.trim()) { setError('Give the transaction type a name.'); return }
    if (example.note) { setError(example.note); return }
    setBusy('save')
    setError('')
    try {
      const saved = await saveTransactionType(typeId, {
        name: name.trim(), description: description.trim(),
        lines: filled.map((l) => ({ side: l.side, account_code: l.account_code, share: l.share, share_value: l.share === 'rest' ? 0 : Number(l.share_value || 0), label: l.label.trim(), can_change: l.can_change })),
      })
      notify(isNew ? `${saved.name} set up` : 'Transaction type saved')
      nav('/accounting/transaction-types')
    } catch (e) {
      setError(e.message || 'Couldn’t save the transaction type.')
    } finally {
      setBusy('')
    }
  }

  const setActive = async (active) => {
    setBusy('active')
    try {
      await setTransactionTypeActive(typeId, active)
      notify(active ? `${type.name} restored` : `${type.name} archived`)
      setType((t) => ({ ...t, is_active: active }))
    } catch (e) {
      setError(e.message || 'Couldn’t change it.')
    } finally {
      setBusy('')
    }
  }

  const remove = async () => {
    setBusy('delete')
    try {
      await deleteTransactionType(typeId)
      notify(`${type.name} deleted`)
      nav('/accounting/transaction-types', { replace: true })
    } catch (e) {
      setConfirmDelete(false)
      setError(e.message || 'Couldn’t delete it.')
    } finally {
      setBusy('')
    }
  }

  if (!type || !accounts) {
    return <div className="ci-form-page"><DocHeader back={back} title="Transaction type" />{error ? <ErrorBanner message={error} /> : <Spinner label="Loading…" />}</div>
  }

  const sideTable = (side) => {
    const sideLines = lines.filter((l) => l.side === side)
    return (
      <div className="ci-form-lines-card" key={side}>
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">{SIDE_TEXT[side].title} <HelpTip text={SIDE_TEXT[side].help} /></div>
        </div>
        <div className="ci-form-table-wrap">
          <table className="ci-form-line-table compact tt-line-table lines-cards">
            <thead>
              <tr>
                <th className="col-account">Account</th>
                <th className="tt-col-share">Share <HelpTip text="How much of the amount this line takes. One line on each side takes the rest, so both sides always add up to the amount." /></th>
                <th className="num tt-col-value">Value</th>
                <th>Label</th>
                <th className="tt-col-change">Can change <HelpTip text="Lets whoever records it choose another account of the same type on this line, for example a different bank account." /></th>
                <th className="col-del" />
              </tr>
            </thead>
            <tbody>
              {sideLines.map((l, i) => (
                <tr key={l.key}>
                  <td data-label="Account">
                    <select className="ci-form-cell-select" value={l.account_code} onChange={(e) => update(l.key, { account_code: e.target.value })} aria-label={`${side} account ${i + 1}`}>
                      <option value="">Choose an account…</option>
                      {l.account_code && !grouped.some(([, list]) => list.some((a) => a.account_code === l.account_code)) && (
                        <option value={l.account_code}>{l.account_code} · {byCode[l.account_code]?.account_name || 'not in the chart'}</option>
                      )}
                      {grouped.map(([label, list]) => (
                        <optgroup key={label} label={label}>
                          {list.map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.account_name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td data-label="Share" className="lc-half">
                    <select className="ci-form-cell-select" value={l.share} onChange={(e) => update(l.key, { share: e.target.value })} aria-label={`${side} share ${i + 1}`}>
                      {SHARES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td data-label="Value" className="lc-half">
                    {l.share === 'rest' ? <span className="tt-rest">What’s left</span> : (
                      <input className="ci-form-cell-input num" value={l.share_value} inputMode="decimal" placeholder={l.share === 'percent' ? '%' : '0.00'}
                        onChange={(e) => update(l.key, { share_value: e.target.value })} aria-label={`${side} value ${i + 1}`} />
                    )}
                  </td>
                  <td data-label="Label"><input className="ci-form-cell-input" value={l.label} maxLength={80} placeholder="Label (optional)" onChange={(e) => update(l.key, { label: e.target.value })} aria-label={`${side} label ${i + 1}`} /></td>
                  <td className="tt-col-change"><input type="checkbox" checked={l.can_change} onChange={(e) => update(l.key, { can_change: e.target.checked })} aria-label={`${side} line ${i + 1} can change`} /><span className="tt-change-text" aria-hidden="true">Can change</span></td>
                  <td className="ci-form-del-cell">
                    <button type="button" className="ci-form-del-btn" disabled={sideLines.length < 2} onClick={() => removeLine(l.key)} aria-label={`Remove ${side} line ${i + 1}`}><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ci-form-add-row">
          <button data-write type="button" className="ci-form-add-line" onClick={() => addLine(side)}><Plus size={14} />Add a {side} line</button>
        </div>
      </div>
    )
  }

  const archived = !isNew && !type.is_active
  return (
    <div className="ci-form-page">
      <DocHeader
        back={back}
        title={isNew ? 'New transaction type' : type.name}
        badge={archived && <StatusBadge label="Archived" tone="muted" />}
        meta={isNew ? 'A kind of transaction you record often, set up once.' : `${type.times_used ? `Recorded ${type.times_used} time${type.times_used === 1 ? '' : 's'}` : 'Not recorded yet'}. Changes apply to what you record from now on.`}
        actions={!isNew && type.is_active && <Link data-write className="ci-btn ci-btn-ghost" to={`/accounting/transaction-types/record?type=${typeId}`}><Send size={15} />Record one</Link>}
      />
      <TransactionTypesGuide />
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top two">
          <label className="ci-form-field">
            <span>Name</span>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gift cards sold" />
          </label>
          <label className="ci-form-field">
            <span>What it’s for (optional)</span>
            <input value={description} maxLength={255} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. A customer buys a gift card at the counter" />
          </label>
        </div>
      </div>

      {sideTable('debit')}
      {sideTable('credit')}

      <section className="doc-card">
        <div className="doc-card-head"><h2>Example</h2><span>What recording {money(EXAMPLE)} would post</span></div>
        {example.note ? <p className="doc-empty">{example.note}</p> : (
          <div className="doc-table-scroll">
            <table className="ci-line-table tt-entry-table">
              <thead><tr><th>Account</th><th className="num">Debit</th><th className="num">Credit</th></tr></thead>
              <tbody>
                {example.lines.map((l) => (
                  <tr key={l.key}>
                    <td>{l.account_code} · {byCode[l.account_code]?.account_name || ''}{l.label && <span className="cell-sub">{l.label}</span>}</td>
                    <td className="num">{l.dr ? money(l.dr) : ''}</td>
                    <td className="num">{l.cr ? money(l.cr) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button data-write type="button" className="ci-btn ci-btn-primary" disabled={!!busy} onClick={save}><Save size={14} />{busy === 'save' ? 'Saving…' : (isNew ? 'Set it up' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/accounting/transaction-types')}>Cancel</button>
        </div>
        {!isNew && (
          <div className="ci-form-action-right">
            {type.is_active
              ? <button data-write type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => setActive(false)}><Archive size={14} />Archive</button>
              : <button data-write type="button" className="ci-btn ci-btn-ghost" disabled={!!busy} onClick={() => setActive(true)}><ArchiveRestore size={14} />Restore</button>}
            {type.times_used === 0 && <button data-write type="button" className="ci-btn ci-btn-ghost ci-btn-danger" disabled={!!busy} onClick={() => setConfirmDelete(true)}><Trash2 size={14} />Delete</button>}
          </div>
        )}
      </div>

      <Modal open={confirmDelete} onClose={() => setConfirmDelete(false)} title={`Delete ${type?.name}?`} size="sm"
        footer={<><Button variant="ghost" onClick={() => setConfirmDelete(false)}>Keep it</Button><Button variant="danger" loading={busy === 'delete'} onClick={remove}>Delete</Button></>}>
        <p className="acc-modal-text">It has never been recorded, so nothing in the books changes.</p>
      </Modal>
    </div>
  )
}
